import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseUTC } from '../utils/dateUtils';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Pagination
} from '@mui/material';
import {
  TrendingUp,
  Language,
  Assessment,
  Visibility,
  FileDownload,
  Group,
  Security,
  ErrorOutline,
  Warning
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  ResponsiveContainer
} from 'recharts';
import ActivityLogsCard from '../components/ActivityLogsCard';
import api from '../services/api';

const IncidentReport = () => {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const fetchIncidents = async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.get(`/sites/all/recent-incidents?days=30&page=${p}&per_page=10`);
      setIncidents(res.data);
    } catch (err) { console.error('Error fetching incidents:', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchIncidents(); }, []);

  const handlePageChange = (e, v) => {
    setPage(v);
    fetchIncidents(v);
  };

  if (loading && !incidents) return null;
  if (!incidents || incidents.total === 0) return null;

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12}>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
          <CardHeader
            title="Incident Report"
            subheader={`${incidents.total} incidents in the last 30 days`}
            titleTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
            subheaderTypographyProps={{ fontSize: '0.8rem' }}
            action={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip label={`${incidents.total} total`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                <IconButton size="small" title="Download CSV"
                  onClick={() => downloadCsv(
                    ['Domain', 'Account', 'Started', 'Resolved', 'Duration (min)', 'Error Type', 'Error', 'Status'],
                    incidents.incidents.map(i => [
                      i.domain_name, i.account_name, i.started_at,
                      i.resolved_at || 'Ongoing', i.duration_minutes || '',
                      i.error_type, i.error, i.is_active ? 'Ongoing' : 'Resolved'
                    ]),
                    `incident-report-${new Date().toISOString().split('T')[0]}.csv`
                  )}
                  sx={{ color: 'text.secondary' }}
                >
                  <FileDownload sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            }
          />
          <CardContent sx={{ pt: 0, maxHeight: 400, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Domain</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Account</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>When</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Error</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incidents.incidents.map((inc) => (
                  <TableRow key={inc.id}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: inc.is_active ? 'rgba(211,47,47,0.04)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(23, 75, 106, 0.04)' },
                      '& td': { borderColor: 'grey.100' }
                    }}
                    onClick={() => navigate(`/domains/${inc.domain_id}`)}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        {inc.domain_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {inc.account_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {parseUTC(inc.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {parseUTC(inc.started_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                        {inc.duration_minutes != null ? `${inc.duration_minutes} min` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Chip label={inc.error_type || 'unknown'} size="small"
                        sx={{
                          height: 18, fontSize: '0.6rem', fontWeight: 600,
                          bgcolor: inc.error_type === 'server_error' ? 'rgba(211,47,47,0.1)' :
                                   inc.error_type === 'timeout' ? 'rgba(198,168,98,0.15)' : 'rgba(211,47,47,0.1)',
                          color: inc.error_type === 'timeout' ? 'warning.dark' : 'error.dark'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>
                      <Chip
                        label={inc.is_active ? 'Ongoing' : 'Resolved'}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 600,
                          bgcolor: inc.is_active ? 'rgba(211,47,47,0.1)' : 'rgba(46,125,50,0.1)',
                          color: inc.is_active ? 'error.dark' : 'success.dark'
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
          {incidents.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, borderTop: '1px solid', borderColor: 'grey.200' }}>
              <Pagination count={incidents.total_pages} page={page} onChange={handlePageChange} size="small" color="primary" />
            </Box>
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

const downloadCsv = (headers, rows, filename) => {
  const escape = (val) => {
    const str = String(val ?? '');
    return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const csv = [headers.join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    totalSites: 0,
    totalSubmissions: 0,
    currentMonthSubmissions: 0,
    currentMonthGF: 0,
    currentMonthCF7: 0,
    monthlyChange: 0,
    gfChange: 0,
    cf7Change: 0,
    uniqueSitesWithForms: 0
  });
  const [loading, setLoading] = useState(true);
  const [topSitesData, setTopSitesData] = useState([]);
  const [formTypesData, setFormTypesData] = useState([
    { name: 'Gravity Forms', value: 0, color: '#174b6a' },
    { name: 'Contact Form 7', value: 0, color: '#82c0c7' }
  ]);
  const [monthlyTrendsData, setMonthlyTrendsData] = useState([]);
  const [activeIncidents, setActiveIncidents] = useState([]);
  const [expiringDomains, setExpiringDomains] = useState([]);
  const [platformStats, setPlatformStats] = useState({
    totalContacts: 0,
    totalSubscriptions: 0,
    totalNervLogins: 0,
    totalWpLogins: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch total sites count
      const sitesResponse = await api.get('/sites');
      const totalSites = sitesResponse.data.length;

      // Fetch form submission stats
      let formStats = null;
      try {
        const formStatsResponse = await api.get('/forms/stats/summary');
        formStats = formStatsResponse.data;
      } catch (err) {
        console.error('Error fetching form stats:', err);
      }

      // Fetch top sites by submissions (current month only)
      let topSites = [];
      try {
        const topSitesResponse = await api.get('/forms/stats/top-sites?limit=10');
        topSites = topSitesResponse.data;
      } catch (err) {
        console.error('Error fetching top sites:', err);
      }

      // Fetch monthly stats
      let monthlyStats = [];
      try {
        const monthlyResponse = await api.get('/forms/stats/monthly?months=6');
        monthlyStats = monthlyResponse.data;
      } catch (err) {
        console.error('Error fetching monthly stats:', err);
      }

      // Fetch platform stats (contacts, subscriptions, logins)
      try {
        const platformStatsResponse = await api.get('/logs/nerv/platform-stats');
        setPlatformStats({
          totalContacts: platformStatsResponse.data.total_contacts || 0,
          totalSubscriptions: platformStatsResponse.data.total_subscriptions || 0,
          totalNervLogins: platformStatsResponse.data.total_nerv_logins || 0,
          totalWpLogins: platformStatsResponse.data.total_wp_logins || 0
        });
      } catch (err) {
        console.error('Error fetching platform stats:', err);
      }

      // Fetch active uptime incidents
      try {
        const incidentsRes = await api.get('/sites/all/active-incidents');
        setActiveIncidents(incidentsRes.data);
      } catch (err) {
        console.error('Error fetching incidents:', err);
      }

      // Fetch expiring domains
      try {
        const expiryRes = await api.get('/sites/all/expiring-domains?days=30');
        setExpiringDomains(expiryRes.data);
      } catch (err) { console.error('Error fetching expiring domains:', err); }

      // Update top sites chart data (top 10 with total submissions only)
      if (topSites.length > 0) {
        const chartData = topSites.slice(0, 10).map(site => ({
          name: site.rootdomain.length > 25
            ? site.rootdomain.substring(0, 23) + '...'
            : site.rootdomain,
          submissions: site.total_submissions
        }));
        setTopSitesData(chartData);
      }

      // Update form types pie chart (this year only)
      if (monthlyStats && monthlyStats.length > 0) {
        const currentYear = new Date().getFullYear();
        const thisYearStats = monthlyStats.filter(stat => stat.year === currentYear);

        const gfTotal = thisYearStats.reduce((sum, s) => sum + (s.gravity_forms_count || 0), 0);
        const cf7Total = thisYearStats.reduce((sum, s) => sum + (s.contact_form_7_count || 0), 0);

        setFormTypesData([
          { name: 'Gravity Forms', value: gfTotal, color: '#174b6a' },
          { name: 'Contact Form 7', value: cf7Total, color: '#82c0c7' }
        ]);
      }

      // Update monthly trends (reverse to show oldest first)
      if (monthlyStats.length > 0) {
        const trendsData = [...monthlyStats].reverse().map(stat => ({
          month: stat.month_name.substring(0, 3),
          submissions: stat.total_submissions,
          sites: stat.unique_sites,
          gravityForms: stat.gravity_forms_count,
          contactForm7: stat.contact_form_7_count
        }));
        setMonthlyTrendsData(trendsData);
      }

      // Calculate current month and last month stats
      const currentMonth = monthlyStats.length > 0 ? monthlyStats[0] : null;
      const lastMonth = monthlyStats.length > 1 ? monthlyStats[1] : null;

      const currentMonthSubmissions = currentMonth?.total_submissions || 0;
      const lastMonthSubmissions = lastMonth?.total_submissions || 0;
      const currentMonthGF = currentMonth?.gravity_forms_count || 0;
      const lastMonthGF = lastMonth?.gravity_forms_count || 0;
      const currentMonthCF7 = currentMonth?.contact_form_7_count || 0;
      const lastMonthCF7 = lastMonth?.contact_form_7_count || 0;

      // Calculate percentage changes
      const monthlyChange = lastMonthSubmissions > 0
        ? Math.round(((currentMonthSubmissions - lastMonthSubmissions) / lastMonthSubmissions) * 100)
        : 0;
      const gfChange = lastMonthGF > 0
        ? Math.round(((currentMonthGF - lastMonthGF) / lastMonthGF) * 100)
        : 0;
      const cf7Change = lastMonthCF7 > 0
        ? Math.round(((currentMonthCF7 - lastMonthCF7) / lastMonthCF7) * 100)
        : 0;

      setDashboardData({
        totalSites: totalSites,
        totalSubmissions: formStats?.total_submissions || 0,
        currentMonthSubmissions: currentMonthSubmissions,
        currentMonthGF: currentMonthGF,
        currentMonthCF7: currentMonthCF7,
        monthlyChange: monthlyChange,
        gfChange: gfChange,
        cf7Change: cf7Change,
        uniqueSitesWithForms: formStats?.total_sites || 0
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };


  const StatCard = ({ title, value, icon, color, trend, subtitle }) => (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Chip
                label={trend}
                size="small"
                color={trend.startsWith('+') ? 'success' : 'error'}
                sx={{ mt: 1, fontSize: '0.75rem' }}
              />
            )}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1"
            sx={{ 
              fontWeight: 700,
              color: 'text.primary',
              mb: 1
            }}
          >
            Dashboard
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            NKP Medical Network Overview
          </Typography>
        </Box>

        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Sites"
              value={loading ? '...' : dashboardData.totalSites}
              icon={<Language />}
              color="primary.main"
              trend={loading ? '' : `${dashboardData.uniqueSitesWithForms} with forms`}
              subtitle="WordPress installations"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Form Submissions"
              value={loading ? '...' : dashboardData.currentMonthSubmissions.toLocaleString()}
              icon={<Assessment />}
              color="secondary.main"
              trend={loading ? '' : (dashboardData.monthlyChange >= 0 ? `+${dashboardData.monthlyChange}%` : `${dashboardData.monthlyChange}%`) + ' vs last month'}
              subtitle="This month"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Gravity Forms"
              value={loading ? '...' : dashboardData.currentMonthGF.toLocaleString()}
              icon={<TrendingUp />}
              color="primary.dark"
              trend={loading ? '' : (dashboardData.gfChange >= 0 ? `+${dashboardData.gfChange}%` : `${dashboardData.gfChange}%`) + ' vs last month'}
              subtitle="This month"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Contact Form 7"
              value={loading ? '...' : dashboardData.currentMonthCF7.toLocaleString()}
              icon={<Visibility />}
              color="success.main"
              trend={loading ? '' : (dashboardData.cf7Change >= 0 ? `+${dashboardData.cf7Change}%` : `${dashboardData.cf7Change}%`) + ' vs last month'}
              subtitle="This month"
            />
          </Grid>
        </Grid>

        {/* Sites Down */}
        {activeIncidents.length > 0 && (
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'error.main', borderRadius: 2, mb: 4 }}>
            <CardHeader
              title="Sites Down"
              subheader={`${activeIncidents.length} ${activeIncidents.length === 1 ? 'site' : 'sites'} currently experiencing issues`}
              titleTypographyProps={{ fontWeight: 600, fontSize: '1rem', color: 'error.main' }}
              subheaderTypographyProps={{ fontSize: '0.8rem' }}
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label={`${activeIncidents.length} down`} size="small"
                    sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: 'error.main', color: 'white' }} />
                  <IconButton size="small" title="Download CSV"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadCsv(
                        ['Domain', 'Account', 'Error Type', 'Error', 'Since', 'Downtime (min)'],
                        activeIncidents.map(i => [i.domain_name, i.account_name, i.error_type, i.error, i.started_at, i.duration_minutes]),
                        `sites-down-${new Date().toISOString().split('T')[0]}.csv`
                      );
                    }}
                    sx={{ color: 'text.secondary' }}
                  >
                    <FileDownload sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              }
            />
            <CardContent sx={{ pt: 0, maxHeight: 300, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Domain</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Account</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Error</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Since</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Downtime</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeIncidents.map((inc) => (
                    <TableRow key={inc.incident_id}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: 'rgba(211,47,47,0.03)',
                        '&:hover': { bgcolor: 'rgba(211,47,47,0.08)' },
                        '& td': { borderColor: 'grey.100' }
                      }}
                      onClick={() => navigate(`/domains/${inc.domain_id}`)}
                    >
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'error.main' }}>
                          {inc.domain_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {inc.account_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Chip label={inc.error_type || 'error'} size="small"
                          sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: 'rgba(211,47,47,0.1)', color: 'error.dark' }} />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {parseUTC(inc.started_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Chip label={`${inc.duration_minutes} min`} size="small"
                          sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: 'error.main', color: 'white' }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Domains Expiring Soon */}
        {expiringDomains.length > 0 && (
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, mb: 4 }}>
            <CardHeader
              title="Domains Expiring Soon"
              subheader={`${expiringDomains.length} active ${expiringDomains.length === 1 ? 'domain' : 'domains'} expiring within 30 days`}
              titleTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
              subheaderTypographyProps={{ fontSize: '0.8rem' }}
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {expiringDomains.some(d => d.status === 'expired') && (
                    <Chip label={`${expiringDomains.filter(d => d.status === 'expired').length} expired`}
                      size="small" color="error" variant="outlined" sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                  <IconButton size="small" title="Download CSV"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadCsv(
                        ['Domain', 'Account', 'Account Status', 'Registrar', 'Expiry Date', 'Days Left', 'Status'],
                        expiringDomains.map(d => [d.domain_name, d.account_name, d.account_status, d.registrar, d.expiry_date, d.days_left, d.status]),
                        `domains-expiring-${new Date().toISOString().split('T')[0]}.csv`
                      );
                    }}
                    sx={{ color: 'text.secondary' }}
                  >
                    <FileDownload sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              }
            />
            <CardContent sx={{ pt: 0, maxHeight: 350, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Domain</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Account</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Acct Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Registrar</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Expires</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 0.75, borderBottom: '2px solid', borderColor: 'grey.200' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expiringDomains.map((domain) => (
                    <TableRow key={domain.domain_id}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: domain.status === 'expired' ? 'rgba(211,47,47,0.04)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(23, 75, 106, 0.04)' },
                        '& td': { borderColor: 'grey.100' }
                      }}
                      onClick={() => navigate(`/domains/${domain.domain_id}`)}
                    >
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                          {domain.domain_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {domain.account_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Chip label={domain.account_status || '—'} size="small"
                          sx={{
                            height: 18, fontSize: '0.6rem', fontWeight: 600,
                            bgcolor: domain.account_status === 'Active' ? 'rgba(46,125,50,0.1)' : 'grey.100',
                            color: domain.account_status === 'Active' ? 'success.dark' : 'text.secondary'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {domain.registrar || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {new Date(domain.expiry_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Chip
                          label={domain.status === 'expired' ? 'Expired' : `${domain.days_left}d left`}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.65rem', fontWeight: 600,
                            bgcolor: domain.status === 'expired' ? 'rgba(211,47,47,0.1)' :
                                     domain.status === 'critical' ? 'rgba(211,47,47,0.1)' : 'rgba(198,168,98,0.15)',
                            color: domain.status === 'expired' ? 'error.dark' :
                                   domain.status === 'critical' ? 'error.dark' : 'warning.dark'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Charts Row 1 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Top Sites by Form Submissions */}
          <Grid item xs={12} lg={8}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
              <CardHeader
                title="Top 10 Sites by Form Submissions"
                subheader="This month"
              />
              <CardContent>
                {topSitesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topSitesData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={180}
                        fontSize={11}
                        tickLine={false}
                      />
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                      <Bar dataKey="submissions" fill="#174b6a" name="Submissions" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography color="text.secondary">
                      {loading ? 'Loading...' : 'No form submission data available'}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Form Types Pie Chart */}
          <Grid item xs={12} lg={4}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
              <CardHeader
                title="Form Plugin Distribution"
                subheader={`This year (${new Date().getFullYear()})`}
              />
              <CardContent>
                {formTypesData.some(d => d.value > 0) ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={formTypesData}
                        cx="50%"
                        cy="40%"
                        labelLine={false}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {formTypesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value, entry) => {
                          const item = formTypesData.find(d => d.name === value);
                          return `${value}: ${item ? item.value.toLocaleString() : 0}`;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography color="text.secondary">
                      {loading ? 'Loading...' : 'No data available'}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Row 2 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Monthly Trends */}
          <Grid item xs={12} lg={8}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', height: 460 }}>
              <CardHeader
                title="Monthly Form Submissions"
                subheader="Submissions trend over the last 6 months"
              />
              <CardContent>
                {monthlyTrendsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={monthlyTrendsData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="submissions"
                        stroke="#174b6a"
                        fill="#174b6a"
                        fillOpacity={0.3}
                        strokeWidth={3}
                        name="Total Submissions"
                      />
                      <Area
                        type="monotone"
                        dataKey="gravityForms"
                        stroke="#0d3249"
                        fill="#0d3249"
                        fillOpacity={0.2}
                        strokeWidth={2}
                        name="Gravity Forms"
                      />
                      <Area
                        type="monotone"
                        dataKey="contactForm7"
                        stroke="#82c0c7"
                        fill="#82c0c7"
                        fillOpacity={0.2}
                        strokeWidth={2}
                        name="Contact Form 7"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography color="text.secondary">
                      {loading ? 'Loading...' : 'No monthly data available yet'}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Activity Logs */}
          <Grid item xs={12} lg={4}>
            <ActivityLogsCard limit={8} />
          </Grid>
        </Grid>

        {/* Incident Report */}
        <IncidentReport />

        {/* Platform Stats */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
              <CardHeader
                title="Platform Statistics"
                subheader="Overview of platform data"
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'primary.light',
                        textAlign: 'center'
                      }}
                    >
                      <Group sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {loading ? '...' : platformStats.totalContacts.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Contacts
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'secondary.light',
                        textAlign: 'center'
                      }}
                    >
                      <Assessment sx={{ color: 'secondary.main', fontSize: 32, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {loading ? '...' : platformStats.totalSubscriptions.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Subscriptions
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'success.light',
                        textAlign: 'center'
                      }}
                    >
                      <Security sx={{ color: 'success.main', fontSize: 32, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {loading ? '...' : platformStats.totalNervLogins.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Nerv Logins
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'warning.light',
                        textAlign: 'center'
                      }}
                    >
                      <Language sx={{ color: 'warning.main', fontSize: 32, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {loading ? '...' : platformStats.totalWpLogins.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        WordPress Logins
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard;